import { PaidCheckoutNotice } from '@/components/subscription/plan-choice/PaidCheckoutNotice';
import { useBillingOffersQuery } from '@/hooks/queries/use-billing-offers-query';
import {
  readBillingTaxProfile,
  useBillingTaxProfileQuery,
} from '@/hooks/queries/use-billing-tax-profile-query';
import { PLAN_IDS } from '@/lib/payload';
import { isPaidCheckoutPending } from '@/libs/billing-access';
import { formatPlanPrice } from '@/libs/utils/format-plan-price';
import { PlanTermsAcceptance } from '@/pages/paywall/_components/PlanTermsAcceptance';
import { usePlanCommitment } from '@/pages/paywall/_components/use-plan-commitment';
import { Button, Stack, Text } from '@chakra-ui/react';
import type { OrganizationBillingContext, User } from '@fluenceur/types';
import { NoticePanel } from '@fluenceur/ui';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { BillingIdentityFields, useBillingIdentity } from './BillingIdentityFields';
import { BillingTaxProfileForm } from './BillingTaxProfileForm';

export function BillingPaidPlanForm({
  user,
  context,
  plan,
  returnTo,
  refresh,
  onCompare,
  unavailable = false,
}: {
  user: User;
  context: OrganizationBillingContext;
  plan: 'essential' | 'growth';
  returnTo?: string;
  refresh: () => Promise<unknown>;
  onCompare: () => void;
  unavailable?: boolean;
}) {
  const { t } = useTranslation();
  const [taxDraftDirty, setTaxDraftDirty] = useState(false);
  const contact = useBillingIdentity(user, context.cardSetup.identityRequired);
  const offers = useBillingOffersQuery({ context });
  const taxProfileQuery = useBillingTaxProfileQuery({ context });
  const taxProfile = readBillingTaxProfile(taxProfileQuery.data);
  const scopedTaxProfile =
    taxProfile?.organizationId === context.organizationId && taxProfile.brandId === context.brandId
      ? taxProfile
      : undefined;
  const taxProfileReady =
    !!scopedTaxProfile?.profile &&
    !taxProfileQuery.isPending &&
    !taxProfileQuery.isFetching &&
    !taxProfileQuery.error;
  const planId = plan === 'essential' ? PLAN_IDS.ESSENTIAL : PLAN_IDS.GROWTH;
  const scopedOffers =
    offers.data?.organizationId === context.organizationId &&
    offers.data.brandId === context.brandId &&
    offers.data.currency === context.billingCurrency
      ? offers.data
      : undefined;
  const offer = scopedOffers?.offers.find((candidate) => candidate.planId === planId);
  const ready =
    !!offer &&
    !offers.isPending &&
    !offers.isFetching &&
    !offers.error &&
    context.checkoutAvailability.paid &&
    context.taxQualificationRequired === false &&
    context.capabilities.canCommitPaidPlan &&
    taxProfileReady &&
    !taxDraftDirty &&
    !unavailable &&
    context.cardSetup.exemption !== 'impersonation';
  const commitment = usePlanCommitment({
    isChangeMode: false,
    hasShopifyIntegration: false,
    isCheckingIntegration: !ready || !contact.valid,
    onMissingIntegration: () => {},
    billingContext: context,
    ...(returnTo ? { returnTo } : {}),
    ...(context.cardSetup.identityRequired ? { identity: contact.identity } : {}),
  });
  const busy = commitment.isCommitting(planId);
  const paidOperation = context.paidCheckout?.status === 'expired' ? null : context.paidCheckout;
  if (commitment.planAwaitingTerms)
    return (
      <PlanTermsAcceptance
        isOpen
        onClose={commitment.dismissTerms}
        onAccepted={commitment.resumeAfterAcceptance}
      />
    );
  if (isPaidCheckoutPending(context, commitment.chargeResult))
    return (
      <PaidCheckoutNotice
        operation={paidOperation}
        onCheck={() => void refresh()}
        busy={busy}
        canResume={ready}
        onResume={paidOperation ? () => commitment.commit(paidOperation.planId) : undefined}
      />
    );
  const showTaxEditor =
    !taxProfileReady || context.taxQualificationRequired !== false || taxDraftDirty;
  return (
    <Stack gap={4}>
      {showTaxEditor && (
        <BillingTaxProfileForm
          context={context}
          onSaved={refresh}
          onDirtyChange={setTaxDraftDirty}
          unavailable={unavailable || busy}
        />
      )}
      {context.taxQualificationRequired !== false && (
        <NoticePanel tone="info" title={t('billing.taxProfile.qualificationPending')} />
      )}
      <NoticePanel tone="info" title={t('planChoice.billing.stripe.title')}>
        {t('planChoice.billing.stripe.sub')}
      </NoticePanel>
      {offer && (
        <Text fontWeight="600">
          {t('planChoice.upgrade', {
            plan: t(`pricing.plan.${plan}.name`),
            price: formatPlanPrice(offer.unitAmount / 100, offer.currency, t('common.locale')),
          })}
        </Text>
      )}
      {offers.error && (
        <NoticePanel
          tone="warning"
          title={t('planChoice.loadError')}
          actions={
            <Button variant="outline" onClick={() => void offers.refetch()}>
              {t('common.retry')}
            </Button>
          }
        />
      )}
      {context.cardSetup.identityRequired && <BillingIdentityFields value={contact} />}
      <Button
        onClick={() => {
          if (ready && contact.valid) commitment.commit(planId);
        }}
        loading={busy}
        disabled={!ready || !contact.valid}
      >
        {t('planChoice.paid.confirmCta')}
      </Button>
      <Button variant="outline" disabled={busy} onClick={onCompare}>
        {t('planChoice.compare')}
      </Button>
    </Stack>
  );
}
